import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const PARTICLE_COUNT = 2000;

interface Particle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  opacity: number;
  opacityDir: number;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export default function HeroSection() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  // Smooth parallax using useSpring
  const { scrollY } = useScroll();
  const rawBgY = useTransform(scrollY, [0, 700], [0, 160]);
  const bgY = useSpring(rawBgY, { stiffness: 80, damping: 20, mass: 0.5 });
  const rawBgScale = useTransform(scrollY, [0, 700], [1, 1.1]);
  const bgScale = useSpring(rawBgScale, { stiffness: 80, damping: 20, mass: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.2 + 0.3,
      speedY: -(0.2 + Math.random() * 0.4),
      opacity: Math.random(),
      opacityDir: Math.random() > 0.5 ? 1 : -1,
    }));

    let lastTime = 0;
    const draw = (time: number) => {
      const delta = Math.min((time - lastTime) / 16.67, 2); // normalize to 60fps
      lastTime = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particlesRef.current) {
        p.y += p.speedY * delta;
        p.opacity += p.opacityDir * 0.005 * delta;
        if (p.opacity >= 0.85) p.opacityDir = -1;
        if (p.opacity <= 0.05) p.opacityDir = 1;
        if (p.y < -2) p.y = canvas.height + 2;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${p.opacity.toFixed(2)})`;
        ctx.fill();
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f1419 0%, #1a1d23 50%, #0f1419 100%)' }}
    >
      {/* Hero Background Image — Smooth Parallax */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <motion.img
          src="https://public.readdy.ai/ai/img_res/bbf93448d75bded1da76259a1b8f0006.jpg"
          alt="NEXNOVA SOLUTIONS technology background"
          className="w-full h-full object-cover object-top will-change-transform"
          style={{ y: bgY, scale: bgScale }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419]/70 via-[#0f1419]/55 to-[#0f1419]/80"></div>
      </div>

      {/* Radial Glows */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(0,212,255,0.12) 0%, transparent 50%),
                            radial-gradient(circle at 80% 80%, rgba(0,212,255,0.08) 0%, transparent 50%),
                            radial-gradient(circle at 40% 20%, rgba(0,212,255,0.06) 0%, transparent 50%)`
        }}></div>
      </div>

      {/* Canvas Particles */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 text-center w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center"
        >
          <motion.div variants={itemVariants}>
            <div className="inline-block mb-6 px-6 py-2 rounded-full bg-[rgba(0,212,255,0.15)] backdrop-blur-sm border border-[rgba(0,212,255,0.3)]">
              <span className="text-[#00d4ff] text-xs font-semibold uppercase tracking-[2px] whitespace-nowrap">
                {t('hero.label')}
              </span>
            </div>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-[900px] mx-auto"
          >
            {t('hero.headline')}
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-[rgba(255,255,255,0.7)] text-lg md:text-xl leading-relaxed mb-12 max-w-[700px] mx-auto"
          >
            {t('hero.subheadline')}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full"
          >
            <Link to="/contact">
              <motion.button
                className="w-full sm:w-auto px-10 py-4 rounded-[28px] bg-[#00d4ff] text-[#0f1419] font-semibold text-base hover:shadow-[0_8px_30px_rgba(0,212,255,0.4)] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                whileHover={{ y: -3, transition: { duration: 0.15, ease: 'easeOut' } }}
                whileTap={{ scale: 0.97 }}
              >
                {t('hero.primaryBtn')}
                <i className="ri-arrow-right-line text-lg"></i>
              </motion.button>
            </Link>

            <motion.button
              onClick={() => scrollToSection('services')}
              className="w-full sm:w-auto px-10 py-4 rounded-[28px] bg-transparent border border-white text-white font-semibold text-base hover:bg-[rgba(255,255,255,0.1)] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
              whileHover={{ y: -3, transition: { duration: 0.15, ease: 'easeOut' } }}
              whileTap={{ scale: 0.97 }}
            >
              {t('hero.secondaryBtn')}
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <i className="ri-arrow-down-line text-[rgba(255,255,255,0.4)] text-3xl"></i>
        </motion.div>
      </div>
    </section>
  );
}